// src/components/ScrollTabs/DoctorScrollTabs.jsx
"use client";

import React, { useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./ScrollTabs.css";

/* ==== Icônes ==== */
const IconWrap = ({ className = "", children }) => (
  <span className={`st-icon ${className}`} aria-hidden="true">
    <svg viewBox="0 0 24 24">{children}</svg>
  </span>
);

const GridIcon = () => (
  <IconWrap>
    <rect x="4" y="4" width="6" height="6" rx="1.5" />
    <rect x="14" y="4" width="6" height="6" rx="1.5" />
    <rect x="4" y="14" width="6" height="6" rx="1.5" />
    <rect x="14" y="14" width="6" height="6" rx="1.5" />
  </IconWrap>
);

const FolderIcon = () => (
  <IconWrap>
    <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6H9l2 2.5h8A1.5 1.5 0 0 1 20.5 10v7A1.5 1.5 0 0 1 19 18.5H5A2 2 0 0 1 3 16.5v-9Z" />
  </IconWrap>
);

const CapsuleIcon = () => (
  <IconWrap className="st-icon--pill st-icon--big">
    <g transform="rotate(-28 12 12)">
      <rect x="5" y="7" width="14" height="10" rx="5" />
      <line x1="12" y1="7" x2="12" y2="17" />
    </g>
  </IconWrap>
);

const norm = (s) => String(s || "").toLowerCase().replace(/\/+$/, "");

/**
 * Props (comme ScrollTabs patient)
 * - mode: "sticky" | "fixed" (default: "sticky")
 * - patientId?: string
 * - headerOffset?: number (default 64)
 *
 * ✅ NEW:
 * - activeKey?: string (contrôlé)
 * - onChange?: (href:string)=>void
 * - forceActiveKey?: string (force un onglet actif)
 */
export default function DoctorScrollTabs({
  mode = "sticky",
  patientId: patientIdProp,
  headerOffset = 64,
  activeKey: activeKeyProp,
  onChange,
  forceActiveKey,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");

  // ✅ fallback patientId depuis URL (si prop absent)
  const patientIdFromUrl =
    params.patientId || params.id || params.userId || params.patient_id;

  const safePatientId = String(patientIdProp || patientIdFromUrl || "").trim();
  const canNavigate = Boolean(safePatientId);

  /* =========================
     ✅ BASE ROUTES
     =========================
     IMPORTANT:
     - Medical Record tab doit matcher:
       /doctor/patients/:id/medicalrecord
       /doctor/patients/:id/medicalrecord/:type
       (donc on utilise medicalrecordRoot)
  */
  const baseRoutes = useMemo(() => {
    if (!canNavigate) {
      return { overviews: "", medicalrecordRoot: "", medicines: "" };
    }
    const base = `/doctor/patients/${safePatientId}`;
    return {
      overviews: `${base}/DoctorOverviewsPage`,
      medicalrecordRoot: `${base}/DoctorMedicalRecordPage`,
      medicines: `${base}/DoctorMedicinesPage`,
    };
  }, [canNavigate, safePatientId]);

  /* =========================
     ✅ TABS CONFIG (keys fixes)
     ========================= */
  const tabs = useMemo(
    () => [
      {
        key: "overviews",
        icon: <GridIcon />,
        label: t("doctorTabs.overview"),
        href: canNavigate ? baseRoutes.overviews : null,
      },
      {
        key: "medicalrecord",
        icon: <FolderIcon />,
        label: t("doctorTabs.medicalRecord"),
        href: canNavigate ? baseRoutes.medicalrecordRoot : null,
      },
      {
        key: "medicines",
        icon: <CapsuleIcon />,
        label: t("doctorTabs.medicines"),
        href: canNavigate ? baseRoutes.medicines : null,
      },
    ],
    [canNavigate, baseRoutes, t]
  );

  /* =========================
     ✅ ACTIVE TAB DETECTION
     - garde MedicalRecord actif pour toutes les sous-pages /medicalrecord/*
     ========================= */
  const computedActiveKey = useMemo(() => {
    if (!canNavigate) return "overviews";

    const p = norm(pathname);
    const ov = norm(baseRoutes.overviews);
    const rec = norm(baseRoutes.medicalrecordRoot);
    const meds = norm(baseRoutes.medicines);

    // ✅ priorité: medicalrecord > medicines > overviews
    if (rec && p.startsWith(rec)) return "medicalrecord";
    if (meds && p.startsWith(meds)) return "medicines";
    if (ov && p.startsWith(ov)) return "overviews";

    // fallback si tu changes tes routes
    if (p.includes("/medicalrecord") || p.includes("/records") || p.includes("record"))
      return "medicalrecord";
    if (p.includes("medicines") || p.includes("medicine")) return "medicines";

    return "overviews";
  }, [pathname, canNavigate, baseRoutes]);

  // ✅ priorité: forceActiveKey > activeKeyProp > computed
  const activeKey = forceActiveKey || activeKeyProp || computedActiveKey;

  const handleTabClick = useCallback(
    (href) => {
      if (!href) return;
      if (onChange) return onChange(href);
      navigate(href);
    },
    [onChange, navigate]
  );

  return (
    <>
      <nav
        className={`scrolltabs ${
          mode === "fixed" ? "scrolltabs--fixed" : "scrolltabs--sticky"
        }`}
        style={{ "--header-offset": `${headerOffset}px` }}
        role="tablist"
        dir={isRTL ? "rtl" : "ltr"}
        aria-label={t("doctorTabs.ariaLabel")}
      >
        <ul className="st-list st-list--3">
          {tabs.map((tab) => {
            const isActive = tab.key === activeKey;

            return (
              <li key={tab.key} className="st-item">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={tab.label}
                  disabled={!canNavigate}
                  className={`st-btn ${isActive ? "is-active" : ""}`}
                  onClick={() => handleTabClick(tab.href)}
                  title={!canNavigate ? t("doctorTabs.disabledHint") : tab.label}
                >
                  <span className="scrolltabs__label">
                    {tab.icon}
                    <span>{tab.label}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {mode === "fixed" && <div className="scrolltabs-spacer" />}
    </>
  );
}